import ApplicationController from './application_controller'

export default class extends ApplicationController {
  static targets = ["row", "notes", "checkbox", "statusButton", "statusDisplay"]
  static values = { validationType: String }
  
  connect() {
    console.log("ValidationModal controller connected", "validationType:", this.validationTypeValue)
    this.notesTimeout = {}
    this.currentExpandedChunk = null
  }
  
  // Toggle checklist for a room
  toggleChecklist(event) {
    const header = event.currentTarget
    const card = header.closest('.validation-room-card')
    const chunkId = card.dataset.chunkId
    
    // Close previously expanded checklist
    if (this.currentExpandedChunk && this.currentExpandedChunk !== chunkId) {
      const prevCard = this.element.querySelector(`[data-chunk-id="${this.currentExpandedChunk}"]`)
      if (prevCard) {
        prevCard.classList.remove('expanded')
      }
    }
    
    // Toggle current checklist
    card.classList.toggle('expanded')
    this.currentExpandedChunk = card.classList.contains('expanded') ? chunkId : null
  }
  
  disconnect() {
    // Clean up timeouts
    Object.values(this.notesTimeout).forEach(timeout => clearTimeout(timeout))
  }
  
  // Handle checkbox change
  checkboxChanged(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const checkbox = event.target
    const chunkId = checkbox.dataset.chunkId
    const itemIndex = checkbox.dataset.itemIndex
    const isChecked = checkbox.checked
    
    console.log('Checkbox changed for chunk:', chunkId, 'item:', itemIndex, 'checked:', isChecked)
    
    // Save after a brief delay to ensure DOM is updated
    setTimeout(() => {
      this.saveChecklistOnly(chunkId)
    }, 0)
  }
  
  // Insert a default/recurring note text into the notes field
  insertDefaultNote(event) {
    event.preventDefault()
    event.stopPropagation()

    const chunkId = event.target.dataset.chunkId
    const noteText = event.target.dataset.noteText
    const card = this.element.querySelector(`.validation-room-card[data-chunk-id="${chunkId}"]`)
    const textarea = card ? card.querySelector('.notes-field') : null
    if (!textarea) return

    textarea.value = textarea.value.trim().length > 0 ? `${textarea.value.trim()}\n${noteText}` : noteText

    clearTimeout(this.notesTimeout[chunkId])
    this.notesTimeout[chunkId] = setTimeout(() => {
      this.saveNotesOnly(chunkId)
    }, 1000)
  }

  // Handle notes input with debounce
  notesChanged(event) {
    const textarea = event.target
    const chunkId = textarea.dataset.chunkId
    
    clearTimeout(this.notesTimeout[chunkId])
    this.notesTimeout[chunkId] = setTimeout(() => {
      this.saveNotesOnly(chunkId)
    }, 1000)
  }
  
  // Handle status button click
  async setStatus(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const btn = event.target.closest('.set-status-btn')
    if (!btn) return
    
    const chunkId = btn.dataset.chunkId
    let validationId = btn.dataset.validationId
    if (!validationId || validationId === '') {
      validationId = null
    }
    const status = btn.dataset.status
    const validationType = btn.dataset.validationType
    
    const card = this.element.querySelector(`.validation-room-card[data-chunk-id="${chunkId}"]`)
    const notesField = card.querySelector('.notes-field')
    const notes = notesField ? notesField.value : ''
    
    // Collect checklist data
    const checklistData = {}
    const checkboxes = this.element.querySelectorAll(`.checklist-checkbox[data-chunk-id="${chunkId}"]`)
    checkboxes.forEach(checkbox => {
      checklistData[checkbox.dataset.itemIndex] = checkbox.checked
    })
    
    // Check if all checkboxes are checked when trying to set to completed
    if (status === 'completed') {
      const allChecked = Array.from(checkboxes).every(cb => cb.checked)
      
      if (!allChecked) {
        const confirmed = confirm('לא כל הפריטים בצ\'קליסט מסומנים. האם עשית בדיקה לפי רשימת הבדיקה? אם תאשר, כל הפריטים יסומנו אוטומטית והמשימה תוגדר כהושלמה.')
        
        if (confirmed) {
          // Mark all checkboxes as checked
          checkboxes.forEach(checkbox => {
            checkbox.checked = true
            checklistData[checkbox.dataset.itemIndex] = true
          })
        } else {
          return // User cancelled
        }
      }
    }
    
    const url = validationId ? `/validation_records/${validationId}` : '/validation_records'
    const method = validationId ? 'PATCH' : 'POST'
    
    console.log('Setting status:', { chunkId, validationId, status, validationType, checklistData })
    
    try {
      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          validation_record: {
            chunk_id: chunkId,
            validation_type: validationType,
            status: status,
            notes: notes,
            checklist_data: checklistData
          }
        })
      })
      
      if (response.ok) {
        const data = await response.json()
        console.log('Status saved successfully:', data)
        
        // Update all buttons with new validation ID
        this.element.querySelectorAll(`[data-chunk-id="${chunkId}"][data-validation-id]`).forEach(el => {
          el.dataset.validationId = data.record.id
        })
        
        // Update status display
        const statusDisplay = card.querySelector('.current-status')
        statusDisplay.className = `current-status status-${status}`
        if (status === 'in_progress') {
          statusDisplay.innerHTML = '⚠️ בעבודה'
        } else if (status === 'completed') {
          statusDisplay.innerHTML = '✅ הושלם'
        } else {
          statusDisplay.innerHTML = '❌ לא נבדק'
        }
        
        // Update card classes
        card.classList.remove('status-unchecked', 'status-in_progress', 'status-completed')
        card.classList.add(`status-${status}`)
        
        // Show success message
        this.showSuccessMessage('נשמר בהצלחה!')
        
        // Update main page tasks using Reflex (no page reload, don't close modal)
        const dateInput = document.querySelector('.date-input')
        const currentDate = dateInput ? dateInput.value : new Date().toISOString().split('T')[0]
        
        // Trigger reflex to update tasks list (but keep modal open)
        try {
        this.stimulate('Calendar#update_validation_status', 
          chunkId,
          data.record.id || '',
          status,
          validationType,
          notes,
          JSON.stringify(checklistData),
          currentDate
        )
        } catch (reflexError) {
          console.error('Reflex error:', reflexError)
          // If reflex fails, just update the UI without reloading
          // Don't reload - keep modal open
        }
      } else {
        const errorData = await response.json().catch(() => ({}))
        console.error('Failed to save status:', response.status, errorData)
        alert('שגיאה בשמירה: ' + (errorData.error || 'שגיאה לא ידועה'))
      }
    } catch (error) {
      console.error('Error:', error)
      alert('שגיאה בשמירה: ' + error.message)
    }
  }
  
  // Save notes only (without changing status)
  async saveNotesOnly(chunkId) {
    const card = this.element.querySelector(`.validation-room-card[data-chunk-id="${chunkId}"]`)
    let validationId = card.dataset.validationId
    if (!validationId || validationId === '') {
      validationId = null
    }
    const notesField = card.querySelector('.notes-field')
    const notes = notesField ? notesField.value : ''
    
    // Get current status
    const statusDisplay = card.querySelector('.current-status')
    let status = 'unchecked'
    if (statusDisplay.classList.contains('status-in_progress')) {
      status = 'in_progress'
    } else if (statusDisplay.classList.contains('status-completed')) {
      status = 'completed'
    }
    
    // Collect checklist data
    const checklistData = {}
    this.element.querySelectorAll(`.checklist-checkbox[data-chunk-id="${chunkId}"]`).forEach(checkbox => {
      checklistData[checkbox.dataset.itemIndex] = checkbox.checked
    })
    
    if (!validationId) {
      // Create new validation record if it doesn't exist
      await this.createValidationRecord(chunkId, this.validationTypeValue, status, notes, checklistData, card)
      return
    }
    
    const url = `/validation_records/${validationId}`
    
    try {
      await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          validation_record: {
            notes: notes,
            checklist_data: checklistData,
            status: status
          }
        })
      })
    } catch (error) {
      console.error('Error saving notes:', error)
    }
  }
  
  // Save checklist only (without changing status)
  async saveChecklistOnly(chunkId) {
    const card = this.element.querySelector(`.validation-room-card[data-chunk-id="${chunkId}"]`)
    let validationId = card.dataset.validationId
    if (!validationId || validationId === '') {
      validationId = null
    }
    const notesField = card.querySelector('.notes-field')
    const notes = notesField ? notesField.value : ''
    
    // Get current status
    const statusDisplay = card.querySelector('.current-status')
    let status = 'unchecked'
    if (statusDisplay.classList.contains('status-in_progress')) {
      status = 'in_progress'
    } else if (statusDisplay.classList.contains('status-completed')) {
      status = 'completed'
    }
    
    // Collect checklist data
    const checklistData = {}
    this.element.querySelectorAll(`.checklist-checkbox[data-chunk-id="${chunkId}"]`).forEach(checkbox => {
      checklistData[checkbox.dataset.itemIndex] = checkbox.checked
    })
    console.log('saveChecklistOnly - Saving checklist data for chunk:', chunkId, checklistData)
    
    if (!validationId) {
      // Create new validation record if it doesn't exist
      await this.createValidationRecord(chunkId, this.validationTypeValue, status, notes, checklistData, card)
      return
    }
    
    const url = `/validation_records/${validationId}`
    
    try {
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          validation_record: {
            notes: notes,
            checklist_data: checklistData,
            status: status
          }
        })
      })
      
      if (response.ok) {
        console.log('Checklist saved successfully for chunk:', chunkId)
        this.showSaveIndicator(card, 'נשמר ✓')
      } else {
        const errorData = await response.json().catch(() => ({}))
        console.error('Failed to save checklist:', response.status, errorData)
        this.showSaveIndicator(card, '❌ שגיאה')
      }
    } catch (error) {
      console.error('Error saving checklist:', error)
      this.showSaveIndicator(card, '❌ שגיאה')
    }
  }
  
  // Create a new validation record
  async createValidationRecord(chunkId, validationType, status, notes, checklistData, card) {
    console.log('Creating new validation record:', { chunkId, validationType, status, checklistData })
    
    try {
      const response = await fetch('/validation_records', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          validation_record: {
            chunk_id: chunkId,
            validation_type: validationType,
            status: status,
            notes: notes,
            checklist_data: checklistData
          }
        })
      })
      
      if (response.ok) {
        const data = await response.json()
        if (data.record && data.record.id) {
          card.dataset.validationId = data.record.id
          // Update all buttons with new validation ID
          this.element.querySelectorAll(`[data-chunk-id="${chunkId}"][data-validation-id]`).forEach(el => {
            el.dataset.validationId = data.record.id
          })
          console.log('Validation record created:', data.record.id)
          this.showSaveIndicator(card, 'נשמר ✓')
        } else {
          console.error('No record ID in response:', data)
        }
      } else {
        const errorData = await response.json().catch(() => ({}))
        console.error('Failed to create validation record:', response.status, errorData)
        this.showSaveIndicator(card, '❌ שגיאה')
      }
    } catch (error) {
      console.error('Error creating validation:', error)
      this.showSaveIndicator(card, '❌ שגיאה')
    }
  }
  
  // Show save indicator
  showSaveIndicator(element, message) {
    // Remove existing indicator if any
    const existing = element.querySelector('.save-indicator')
    if (existing) existing.remove()
    
    const indicator = document.createElement('span')
    indicator.className = 'save-indicator'
    indicator.textContent = message
    indicator.style.cssText = 'color: #10b981; font-size: 0.85em; margin-right: 10px; animation: fadeIn 0.3s ease;'
    
    const roomInfo = element.querySelector('.room-info')
    if (roomInfo) {
      roomInfo.appendChild(indicator)
    }
    
    setTimeout(() => {
      indicator.style.opacity = '0'
      indicator.style.transition = 'opacity 0.3s'
      setTimeout(() => indicator.remove(), 300)
    }, 2000)
  }
  
  // Show success message
  showSuccessMessage(message) {
    const successMsg = document.createElement('div')
    successMsg.textContent = message
    successMsg.style.cssText = 'position: fixed; top: 20px; right: 20px; background: #10b981; color: white; padding: 10px 20px; border-radius: 5px; z-index: 10000;'
    document.body.appendChild(successMsg)
    setTimeout(() => {
      successMsg.remove()
    }, 2000)
  }
  
  // Mark all equipment as returned
  async markAllReturned(event) {
    event.preventDefault()
    event.stopPropagation()
    
    const btn = event.target.closest('.mark-all-returned-btn')
    if (!btn) return
    
    const date = btn.dataset.date
    
    if (!confirm('האם לסמן את כל הציוד שמחכה לזיכוי כהוחזר? פעולה זו תסמן את כל פריטי הציוד כהוחזרו ותנקה את הרשימה.')) {
      return
    }
    
    btn.disabled = true
    btn.textContent = 'מסמן...'
    
    try {
      const response = await fetch('/validation_records/mark_all_equipment_returned', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({
          date: date
        })
      })
      
      if (response.ok) {
        const data = await response.json()
        this.showSuccessMessage(data.message || 'כל הציוד סומן כהוחזר בהצלחה!')
        
        // Reload the modal to show updated list
        const dateInput = document.querySelector('.date-input')
        const currentDate = dateInput ? dateInput.value : date
        
        // Reload the modal content directly
        const modalContent = document.querySelector('#calendar-modal-content')
        if (modalContent) {
          fetch(`/validation_records?validation_type=equipment_credit&date=${currentDate}`, {
            headers: {
              'Accept': 'text/html',
              'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
            }
          })
          .then(response => response.text())
          .then(html => {
            modalContent.innerHTML = html
            // Re-initialize the validation modal controller
            const validationModal = modalContent.querySelector('[data-controller*="validation-modal"]')
            if (validationModal) {
              // The Stimulus controller should auto-connect
            }
          })
          .catch(error => {
            console.error('Error reloading modal:', error)
            window.location.reload()
          })
        } else {
          window.location.reload()
        }
      } else {
        const errorData = await response.json().catch(() => ({}))
        alert('שגיאה בסימון הציוד: ' + (errorData.error || 'שגיאה לא ידועה'))
        btn.disabled = false
        btn.textContent = '✅ סמן שהכל הוחזר'
      }
    } catch (error) {
      console.error('Error:', error)
      alert('שגיאה בסימון הציוד: ' + error.message)
      btn.disabled = false
      btn.textContent = '✅ סמן שהכל הוחזר'
    }
  }
}

